import { useEffect, useRef, useState } from 'react';
import { BlockNoteSchema, defaultBlockSpecs, defaultStyleSpecs } from '@blocknote/core';
import { useCreateBlockNote } from '@blocknote/react';

import {
  applyBracketHighlightPlugin,
  type BracketMatch,
  type OnBracketClick,
} from '../utils/blocknote/bracketHighlightPlugin';
import { applyDiffPlugin, type DiffOperation,removeDiffPlugin } from '../utils/blocknote/diffHighlightPlugin';
import { applyMisspellingPlugin } from '../utils/blocknote/misspellingPlugin';
import { injectBlockNoteStyles } from '../utils/blocknote/styles';
import { type AnyBlockNoteEditor } from '../utils/blocknote/types';
import {
  extractMarkdownFromCodeBlocks,
  normalizeMarkdownInput,
  normalizeMarkdownNewlines,
} from '../utils/utils';

// Create custom schema without code blocks or inline code
// This prevents code blocks and inline code from being rendered, created, or parsed from markdown
const { codeBlock: _codeBlock, ...remainingBlockSpecs } = defaultBlockSpecs;
const { code: _code, ...remainingStyleSpecs } = defaultStyleSpecs;
const customSchema = BlockNoteSchema.create({
  blockSpecs: remainingBlockSpecs,
  styleSpecs: remainingStyleSpecs,
});

interface UseBlockNoteEditorOptions {
  /**
   * Initial markdown content to load into the editor
   */
  initialMarkdown?: string;

  /**
   * Whether to enable the bracket highlight plugin
   */
  enableBracketHighlight?: boolean;

  /**
   * Callback when a bracket is clicked (required if enableBracketHighlight is true)
   */
  onBracketClick?: OnBracketClick;

  /**
   * Callback fired (on mount and on every edit) with the current bracket
   * placeholder matches in this editor, for note-level aggregation.
   */
  onBracketsChanged?: (matches: BracketMatch[]) => void;

  /**
   * Whether to enable the misspelling plugin
   */
  enableMisspelling?: boolean;

  /**
   * Array of misspelled words to highlight (required if enableMisspelling is true)
   */
  misspelledWords?: string[];

  /**
   * Callback when a misspelled word is clicked
   */
  onWordClick?: (word: string) => void;

  /**
   * Array of trouble words with correct spellings
   */
  troubleWords?: Array<{ correct_spelling: string; misspellings: string[] }>;

  /**
   * Whether to enable the diff highlight plugin
   */
  enableDiff?: boolean;

  /**
   * Array of diff operations to highlight (required if enableDiff is true)
   */
  diff?: DiffOperation[];

  /**
   * Callback when editor is ready
   */
  onEditorReady?: (editor: AnyBlockNoteEditor) => void;

  /**
   * Unique style ID to avoid style conflicts (default: 'blocknote-shared-styles')
   */
  styleId?: string;
}

/**
 * Custom hook for creating and managing a BlockNote editor instance
 * Handles initialization, markdown parsing, style injection, and plugin management
 */
export const useBlockNoteEditor = (options: UseBlockNoteEditorOptions = {}) => {
  const {
    initialMarkdown,
    enableBracketHighlight = false,
    onBracketClick,
    onBracketsChanged,
    enableMisspelling = false,
    misspelledWords = [],
    onWordClick,
    troubleWords,
    enableDiff = false,
    diff,
    onEditorReady,
    styleId = 'blocknote-shared-styles',
  } = options;

  const editor = useCreateBlockNote({
    schema: customSchema,
  });
  const [isReady, setIsReady] = useState(false);
  const cleanupStylesRef = useRef<(() => void) | null>(null);

  // Initialize editor with content and apply styles
  useEffect(() => {
    const initializeEditor = async () => {
      try {
        // Inject shared styles
        cleanupStylesRef.current = injectBlockNoteStyles(styleId);

        // Process text: normalize input, extract from code blocks, then normalize newlines
        const safeMarkdown = initialMarkdown ?? '';
        let processedText = normalizeMarkdownInput(safeMarkdown);
        processedText = extractMarkdownFromCodeBlocks(processedText);
        // Normalize newlines to preserve single line breaks as paragraph breaks
        processedText = normalizeMarkdownNewlines(processedText);

        // Parse markdown to blocks and update the editor
        if (processedText) {
          const blocks = await editor.tryParseMarkdownToBlocks(processedText);
          // Ensure blocks array doesn't contain undefined elements
          const validBlocks = blocks.filter(block => block != null);
          // Ensure we have at least one block (BlockNote requires at least one block)
          if (validBlocks.length > 0) {
            editor.replaceBlocks(editor.document, validBlocks);
          } else {
            // If no valid blocks, ensure we have at least an empty paragraph
            editor.replaceBlocks(editor.document, [
              {
                type: 'paragraph',
                content: '',
              } as any,
            ]);
          }
        } else {
          // Ensure editor has at least one empty block (BlockNote requires at least one block)
          // Check if document is already empty to avoid unnecessary updates
          if (editor.document.length === 0 || editor.document.every(b => !b)) {
            editor.replaceBlocks(editor.document, [
              {
                type: 'paragraph',
                content: '',
              } as any,
            ]);
          }
        }

        setIsReady(true);

        // Notify parent component that editor is ready
        if (onEditorReady) {
          onEditorReady(editor);
        }
      } catch (err) {
        console.error('Error initializing BlockNote editor:', err);
        setIsReady(true); // Still mark as ready to avoid blocking UI
      }
    };

    initializeEditor();

    // Clean up styles on unmount
    return () => {
      if (cleanupStylesRef.current) {
        cleanupStylesRef.current();
        cleanupStylesRef.current = null;
      }
    };
    // Note: initialMarkdown intentionally excluded from deps to avoid re-init on every change
    // onEditorReady is also excluded as it's a callback that shouldn't trigger re-init
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, styleId]);

  // Update editor content when markdown changes (separate effect to avoid re-initialization)
  useEffect(() => {
    if (!isReady) return;

    const updateContent = async () => {
      try {
        const safeMarkdown = initialMarkdown ?? '';
        let processedText = normalizeMarkdownInput(safeMarkdown);
        processedText = extractMarkdownFromCodeBlocks(processedText);
        // Normalize newlines to preserve single line breaks as paragraph breaks
        processedText = normalizeMarkdownNewlines(processedText);
        if (processedText) {
          const blocks = await editor.tryParseMarkdownToBlocks(processedText);
          // Ensure blocks array doesn't contain undefined elements
          const validBlocks = blocks.filter(block => block != null);
          // Ensure we have at least one block
          if (validBlocks.length > 0) {
            editor.replaceBlocks(editor.document, validBlocks);
          } else {
            editor.replaceBlocks(editor.document, [
              {
                type: 'paragraph',
                content: '',
              } as any,
            ]);
          }
        } else {
          // Ensure editor has at least one empty block
          editor.replaceBlocks(editor.document, [
            {
              type: 'paragraph',
              content: '',
            } as any,
          ]);
        }
      } catch (err) {
        console.error('Error updating editor content:', err);
        // Fallback: ensure editor has at least one valid block
        try {
          editor.replaceBlocks(editor.document, [
            {
              type: 'paragraph',
              content: '',
            } as any,
          ]);
        } catch (fallbackErr) {
          console.error('Error in fallback editor update:', fallbackErr);
        }
      }
    };

    updateContent();
  }, [editor, initialMarkdown, isReady]);

  // Apply bracket highlight plugin when enabled
  // This effect runs whenever enableBracketHighlight or onBracketClick changes
  // Important: Plugin is reapplied to ensure it's active, even if editor content was updated
  useEffect(() => {
    if (!isReady || !enableBracketHighlight) {
      return;
    }

    // Apply plugin immediately - no delay needed since editor is already ready
    // The plugin application handles re-applying safely (removes old, adds new)
    applyBracketHighlightPlugin(editor, onBracketClick, onBracketsChanged);
  }, [editor, enableBracketHighlight, onBracketClick, onBracketsChanged, isReady]);

  // Apply misspelling plugin when enabled and words are available
  // This effect runs whenever enableMisspelling or misspelledWords change
  // Important: Plugin is reapplied to ensure it's active, even if editor content was updated
  useEffect(() => {
    if (!isReady || !enableMisspelling || !misspelledWords || misspelledWords.length === 0) {
      return;
    }

    // Apply plugin immediately - no delay needed since editor is already ready
    // The plugin application handles re-applying safely (removes old, adds new)
    applyMisspellingPlugin(editor, misspelledWords, onWordClick, troubleWords);
  }, [editor, enableMisspelling, misspelledWords, onWordClick, troubleWords, isReady]);

  // Apply diff plugin when enabled and diff data is available
  // This effect runs whenever enableDiff or diff changes
  // Important: Plugin is reapplied to ensure it's active, even if editor content was updated
  useEffect(() => {
    if (!isReady) {
      return;
    }

    // If enableDiff is false or no diff data, remove the plugin
    if (!enableDiff || !diff || diff.length === 0) {
      removeDiffPlugin(editor);
      return;
    }

    // Apply plugin immediately - no delay needed since editor is already ready
    // The plugin application handles re-applying safely (removes old, adds new)
    applyDiffPlugin(editor, diff);
  }, [editor, enableDiff, diff, isReady]);

  /**
   * Update the editor content with new markdown
   */
  const setMarkdown = async (markdown: string) => {
    try {
      const safeMarkdown = markdown ?? '';
      let processedText = normalizeMarkdownInput(safeMarkdown);
      processedText = extractMarkdownFromCodeBlocks(processedText);
      // Normalize newlines to preserve single line breaks as paragraph breaks
      processedText = normalizeMarkdownNewlines(processedText);
      if (processedText) {
        const blocks = await editor.tryParseMarkdownToBlocks(processedText);
        // Ensure blocks array doesn't contain undefined elements
        const validBlocks = blocks.filter(block => block != null);
        // Ensure we have at least one block
        if (validBlocks.length > 0) {
          editor.replaceBlocks(editor.document, validBlocks);
        } else {
          editor.replaceBlocks(editor.document, [
            {
              type: 'paragraph',
              content: '',
            } as any,
          ]);
        }
      } else {
        // Ensure editor has at least one empty block
        editor.replaceBlocks(editor.document, [
          {
            type: 'paragraph',
            content: '',
          } as any,
        ]);
      }
    } catch (err) {
      console.error('Error setting markdown:', err);
      // Fallback: ensure editor has at least one valid block
      try {
        editor.replaceBlocks(editor.document, [
          {
            type: 'paragraph',
            content: '',
          } as any,
        ]);
      } catch (fallbackErr) {
        console.error('Error in fallback setMarkdown:', fallbackErr);
      }
    }
  };

  /**
   * Get the current markdown content from the editor
   */
  const getMarkdown = async (): Promise<string> => {
    try {
      return editor.blocksToMarkdownLossy(editor.document);
    } catch (err) {
      console.error('Error getting markdown:', err);
      return '';
    }
  };

  return {
    editor,
    isReady,
    setMarkdown,
    getMarkdown,
  };
};
